/**
 * Leitura de cota para gente que não é hidrólogo — funções puras (testadas em
 * `nivel.test.ts`).
 *
 * Duas armadilhas de comunicação moldam este arquivo:
 *
 * 1. **Cota não é profundidade nem altitude.** É a leitura de uma régua cujo
 *    zero é uma referência local arbitrária. "Óbidos 3,19 m" num rio com
 *    dezenas de metros de fundo é uma frase que se lê errado com facilidade —
 *    por isso o rótulo é sempre "cota (régua local)", nunca "altura" ou
 *    "profundidade".
 * 2. **Cota não é comparável entre estações.** Manaus a 2069 cm e Óbidos a
 *    319 cm não dizem que Manaus tem mais água: são réguas diferentes, em
 *    rios diferentes, com zeros diferentes. O que compara é a POSIÇÃO
 *    PERCENTÍLICA, que é adimensional.
 */
import type { Classe, Referencia, Tendencia } from './types';

/** Centímetros → metros, como a cota é noticiada ("o rio está em 20,69 m"). */
export function cotaM(cm: number | null | undefined): string | null {
  if (typeof cm !== 'number' || !Number.isFinite(cm)) return null;
  return (cm / 100).toFixed(2).replace('.', ',');
}

/** Variação em cm → texto com sinal e unidade legível. */
export function variacaoTexto(cm: number | null, horas = 24): string | null {
  if (cm === null || !Number.isFinite(cm)) return null;
  const sinal = cm > 0 ? '+' : cm < 0 ? '−' : '';
  const abs = Math.abs(cm);
  const valor = abs >= 100 ? `${(abs / 100).toFixed(2).replace('.', ',')} m` : `${Math.round(abs)} cm`;
  return `${sinal}${valor} em ${horas} h`;
}

export const NOME_TENDENCIA: Readonly<Record<Tendencia, string>> = {
  subindo: 'subindo',
  descendo: 'descendo',
  estavel: 'estável',
  desconhecida: 'sem série suficiente',
};

/** Seta da tendência. O traço do "estável" é um traço, não um ponto. */
export const SETA_TENDENCIA: Readonly<Record<Tendencia, string>> = {
  subindo: '▲',
  descendo: '▼',
  estavel: '–',
  desconhecida: '',
};

export const NOME_CLASSE: Readonly<Record<Classe, string>> = {
  muitoAbaixo: 'muito abaixo do normal',
  abaixo: 'abaixo do normal',
  normal: 'dentro do normal',
  acima: 'acima do normal',
  muitoAcima: 'muito acima do normal',
};

/**
 * A frase que dá sentido ao número, em vez do percentil cru.
 *
 * "Percentil 13" não significa nada para quem lê o portal. **"Mais baixo que
 * em 13 dos 15 setembros medidos desde 2014"** é a mesma informação, é
 * verificável, e carrega o tamanho da amostra junto — que é justamente o que
 * um percentil calculado sobre poucos anos precisa carregar para ser honesto.
 */
export function fraseComparativa(percentil: number | null, ref: Referencia | null, desde: number | null): string | null {
  if (percentil === null || !ref || !Number.isFinite(ref.anos) || ref.anos < 1) return null;

  const anos = ref.anos;
  const periodo = desde ? ` desde ${desde}` : '';
  // Quantos anos ficaram abaixo do valor de hoje, aproximado pelo percentil.
  const abaixo = Math.round((percentil / 100) * anos);
  const acima = anos - abaixo;

  if (percentil <= 10) {
    return `Mais baixo que em ${acima} dos ${anos} anos medidos${periodo} nesta época.`;
  }
  if (percentil >= 90) {
    return `Mais alto que em ${abaixo} dos ${anos} anos medidos${periodo} nesta época.`;
  }
  return `Dentro do que os ${anos} anos medidos${periodo} mostram para esta época.`;
}

/**
 * Quanto a cota de hoje difere da mediana daquele dia do ano, em texto.
 * Vem SEMPRE acompanhada da frase comparativa — sozinha, uma diferença em
 * metros volta a ser incomparável entre rios.
 */
export function anomaliaTexto(anomaliaCm: number | null): string | null {
  if (anomaliaCm === null || !Number.isFinite(anomaliaCm)) return null;
  if (Math.abs(anomaliaCm) < 5) return 'praticamente na mediana desta época';
  const abs = Math.abs(anomaliaCm);
  const valor = abs >= 100 ? `${(abs / 100).toFixed(1).replace('.', ',')} m` : `${Math.round(abs)} cm`;
  return `${valor} ${anomaliaCm < 0 ? 'abaixo' : 'acima'} da mediana desta época`;
}

/** Vazão em m³/s, com separador de milhar em português. */
export function vazaoTexto(m3s: number | null): string | null {
  if (m3s === null || !Number.isFinite(m3s) || m3s <= 0) return null;
  return `${Math.round(m3s).toLocaleString('pt-BR')} m³/s`;
}

/**
 * Cota da estação no quadro exibido.
 *
 * `null` quando o quadro está fora da janela publicada — e aí a camada mostra
 * "sem medição neste instante" em vez da última cota conhecida. É o mesmo
 * princípio do `SEM_DADO` das estações de chuva e do `null` da trilha das
 * embarcações: não afirmar o que não se mediu.
 *
 * Quem decide isso é o ÍNDICE, e não uma tolerância em minutos: a série é uma
 * grade regular, então o slot mais próximo nunca está a mais de meio passo de
 * distância. Uma tolerância maior que meio passo seria código que nunca roda,
 * e uma menor criaria buracos dentro da própria janela.
 */
export function cotaNoQuadro(
  serie: readonly (number | null)[] | undefined,
  t0: number,
  passoMin: number,
  instante: number | null,
): { cm: number; ms: number } | null {
  if (!serie) return null;
  const i = indiceNoQuadro(serie.length, t0, passoMin, instante);
  if (i < 0) return null;
  const cm = serie[i];
  if (cm === null || cm === undefined || !Number.isFinite(cm)) return null;
  return { cm, ms: t0 + i * passoMin * 60_000 };
}

/**
 * Última medição da série até o quadro exibido — o que a ficha mostra.
 *
 * O ponto no mapa segue `cotaNoQuadro` e fica vazado quando a hora exibida
 * não tem medição. A ficha, não: mostra sempre a última cota medida, com o
 * horário dela ao lado, porque é o horário que impede de ler a cota como se
 * fosse do quadro. O caso comum é o quadro mais recente do satélite, mais novo
 * que a última leitura telemétrica da ANA.
 *
 * Quadro depois da janela (ou nenhum): a última medição da série. Quadro antes
 * da janela, ou série sem medição até ele: `null` (quem chama usa a leitura
 * mais recente da estação).
 */
export function ultimaMedicao(
  serie: readonly (number | null)[] | undefined,
  t0: number,
  passoMin: number,
  instante: number | null,
): { cm: number; ms: number } | null {
  if (!serie || serie.length === 0 || !(passoMin > 0)) return null;
  const n = serie.length;
  let i = n - 1;
  if (instante !== null && Number.isFinite(instante)) {
    const k = Math.round((instante - t0) / (passoMin * 60_000));
    if (k < 0) return null;
    i = Math.min(k, n - 1);
  }
  for (; i >= 0; i--) {
    const cm = serie[i];
    if (typeof cm === 'number' && Number.isFinite(cm)) return { cm, ms: t0 + i * passoMin * 60_000 };
  }
  return null;
}

/**
 * Hora da grade que corresponde ao quadro exibido, ou -1 fora da janela.
 *
 * É a mesma regra do ponto no mapa (`cotaNoQuadro`), para que a marca do
 * instante no gráfico da ficha caia na hora que o mapa está pintando — mesmo
 * quando essa hora não tem medição.
 */
export function indiceNoQuadro(n: number, t0: number, passoMin: number, instante: number | null): number {
  if (n <= 0 || instante === null || !Number.isFinite(instante) || !(passoMin > 0)) return -1;
  const i = Math.round((instante - t0) / (passoMin * 60_000));
  return i >= 0 && i < n ? i : -1;
}

/** Faixa vertical do gráfico da ficha. */
export interface EscalaCota {
  /** Menor e maior cota medidas na série, em cm. */
  min: number;
  max: number;
  /** Base e altura da faixa desenhada, em cm. */
  base: number;
  amplitude: number;
}

/**
 * Escala do gráfico de 48 h, ou `null` com menos de duas medições — um ponto
 * só não é série.
 *
 * Um rio que não se mexe em 48 h daria uma linha reta colada numa borda, e
 * uma variação de 2 cm esticada até o topo pareceria uma cheia. O piso de
 * 10 cm dá escala sem exagerar, e a faixa fica centrada na variação medida.
 */
export function escalaDaSerie(serie: readonly (number | null)[], pisoCm = 10): EscalaCota | null {
  const medidos = serie.filter((v): v is number => typeof v === 'number' && Number.isFinite(v));
  if (medidos.length < 2) return null;
  const min = Math.min(...medidos);
  const max = Math.max(...medidos);
  const amplitude = Math.max(pisoCm, max - min);
  return { min, max, base: (min + max) / 2 - amplitude / 2, amplitude };
}

/**
 * Trechos contínuos da série: índices consecutivos com medição.
 *
 * A linha se interrompe onde falta medição. Ligar os pontos por cima do
 * buraco desenharia uma cota que ninguém leu — o mesmo cuidado das horas sem
 * medição no gráfico de chuva.
 */
export function trechosDaSerie(serie: readonly (number | null)[]): number[][] {
  const trechos: number[][] = [];
  let atual: number[] = [];
  serie.forEach((v, i) => {
    if (typeof v === 'number' && Number.isFinite(v)) atual.push(i);
    else if (atual.length) {
      trechos.push(atual);
      atual = [];
    }
  });
  if (atual.length) trechos.push(atual);
  return trechos;
}
